import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircleFill, InfoCircleFill, KeyFill, LightningChargeFill, Pencil, QrCode } from "react-bootstrap-icons";
import { Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { IntegrationStatus } from "./Data/ApiTransport";
import {
  ParkerIntegrationPartyApi,
  ServerRequestsParkerIntegrationPartyDto,
} from "./shared/api/axios-client";
import { createApiService, silentAxiosRequest } from "./shared/apiConfig";
import { Button } from "./Controls/Button";
import { EditRfidModal } from "./Controls/EditRfidModal";
import {
  supportsCredentialUpdate, suppressesQr,
  supportsRealTimeSyncCheck,
} from "./Data/IntegrationCapabilities";
import { getIntegrationDisplayName, getIntegrationDescription } from "./Data/IntegrationDisplay";

type SyncStatus = "verifying" | "verified" | "not_verified" | "error";

interface SyncState {
  status: SyncStatus;
  message?: string | null;
  canResync?: boolean;
}

interface Props {
  integrations: ServerRequestsParkerIntegrationPartyDto[];
  subscriptionId: number;
  facilityId: number;
  setPendingToggle: (data: {
    integrationPartyId: number;
    subscriptionId: number;
    parkerId: number;
    facilityId: number;
    newStatus: IntegrationStatus;
  }) => void;
  setShowToggleConfirm: (show: boolean) => void;
  setQrCodeData: (data: string) => void;
  setShowQrModal: (show: boolean) => void;
  setParkerIntegrations?: React.Dispatch<
    React.SetStateAction<ServerRequestsParkerIntegrationPartyDto[]>
  >;
  hasSubParkers?: boolean;
  hasEnrolledIntegrations?: boolean;
  isGroup?: boolean;
}

export function ParkerIntegrations({
  integrations,
  subscriptionId,
  facilityId,
  setPendingToggle,
  setShowToggleConfirm,
  setQrCodeData,
  setShowQrModal,
  setParkerIntegrations,
  hasSubParkers = false,
  hasEnrolledIntegrations = false,
  isGroup = false,
}: Props) {
  const showManagedViaParkersMessage =
    isGroup ||
    (hasSubParkers && !hasEnrolledIntegrations && integrations.length > 0);
  const [editRfidIntegration, setEditRfidIntegration] =
    useState<ServerRequestsParkerIntegrationPartyDto | null>(null);
  const [syncStates, setSyncStates] = useState<Record<number, SyncState>>({});
  const [resyncingId, setResyncingId] = useState<number | null>(null);

  const canEdit = (integration: ServerRequestsParkerIntegrationPartyDto) =>
    supportsCredentialUpdate(integration) &&
    integration.isActive &&
    setParkerIntegrations;

  const canEditRfid = (integration: ServerRequestsParkerIntegrationPartyDto) => {
    if (!canEdit(integration)) return false;
    const id = integration.integrationPartyId;
    if (id == null || !canSyncCheck(integration)) return true;
    const state = syncStates[id];
    return !(
      state?.canResync === false &&
      (state.status === "not_verified" || state.status === "error")
    );
  };

  const canSyncCheck = (integration: ServerRequestsParkerIntegrationPartyDto) =>
    supportsRealTimeSyncCheck(integration) && integration.isActive;

  const handleRfidUpdated = (updated: ServerRequestsParkerIntegrationPartyDto) => {
    if (setParkerIntegrations) {
      setParkerIntegrations((prev) => {
        const keyMatch = (i: ServerRequestsParkerIntegrationPartyDto) =>
          i.integrationPartyId === updated.integrationPartyId &&
          i.parkerId === updated.parkerId &&
          (i.vehicleId ?? undefined) === (updated.vehicleId ?? undefined);
        return prev.some(keyMatch)
          ? prev.map((i) => (keyMatch(i) ? { ...i, ...updated } : i))
          : [...prev, updated];
      });
    }
  };

  const fetchSyncStatus = useCallback(
    async (parkerId: number, integrationPartyId?: number) => {
      const eligible = integrations.filter(canSyncCheck);
      if (eligible.length === 0) return;

      const idsToCheck = integrationPartyId
        ? eligible
            .filter((i) => i.integrationPartyId === integrationPartyId)
            .map((i) => i.integrationPartyId!)
        : eligible.map((i) => i.integrationPartyId!);

      setSyncStates((prev) => {
        const next = { ...prev };
        for (const id of idsToCheck) {
          next[id] = { status: "verifying" };
        }
        return next;
      });

      try {
        const api = createApiService(ParkerIntegrationPartyApi);
        const response = await api.parkerIntegrationPartiesSyncStatusGet(
          {
            facilityId,
            subscriptionId,
            parkerId,
            integrationPartyId,
          },
          silentAxiosRequest
        );

        const items = response.data.items ?? [];
        setSyncStates((prev) => {
          const next = { ...prev };
          for (const id of idsToCheck) {
            const item = items.find(
              (i) =>
                i.integrationPartyId === id &&
                (i.vehicleId == null || i.vehicleId === undefined)
            );
            if (!item) {
              next[id] = { status: "error", message: "Verification unavailable", canResync: false };
            } else if (item.isSynced) {
              next[id] = { status: "verified" };
            } else {
              next[id] = {
                status: "not_verified",
                message: item.message,
                canResync: item.canResync ?? false,
              };
            }
          }
          return next;
        });
      } catch {
        setSyncStates((prev) => {
          const next = { ...prev };
          for (const id of idsToCheck) {
            next[id] = { status: "error", message: "Verification failed", canResync: false };
          }
          return next;
        });
      }
    },
    [integrations, facilityId, subscriptionId]
  );

  const fetchSyncStatusRef = useRef(fetchSyncStatus);
  fetchSyncStatusRef.current = fetchSyncStatus;

  const syncTarget = useMemo(() => {
    const eligible = integrations.filter(canSyncCheck);
    if (eligible.length === 0) return null;

    const parkerId = eligible[0].parkerId;
    if (parkerId == null) return null;

    const signature = eligible
      .map(
        (i) =>
          `${i.integrationPartyId}:${i.isActive}:${i.metaFlashRFIDNumber ?? ""}`
      )
      .sort()
      .join("|");

    return {
      parkerId,
      signature: `${facilityId}:${subscriptionId}:${parkerId}:${signature}`,
    };
  }, [integrations, facilityId, subscriptionId]);

  useEffect(() => {
    if (!syncTarget) return;

    fetchSyncStatusRef.current(syncTarget.parkerId);
  }, [syncTarget?.signature]);

  const handleResync = async (integration: ServerRequestsParkerIntegrationPartyDto) => {
    if (!integration.integrationPartyId || integration.parkerId == null) return;

    const rfid = integration.metaFlashRFIDNumber?.trim();
    if (!rfid) {
      toast.error("No RFID on record to resync");
      return;
    }

    setResyncingId(integration.integrationPartyId);
    try {
      const api = createApiService(ParkerIntegrationPartyApi);
      const response = await api.parkerIntegrationPartiesCredentialUpdatePatch({
        serverRequestsUpdateCredentialRequest: {
          facilityId,
          subscriptionId,
          parkerId: integration.parkerId,
          integrationPartyId: integration.integrationPartyId,
          updatedCredential: rfid,
        },
      });

      if (response.data.success && response.data.parkerIntegrationParty) {
        handleRfidUpdated(response.data.parkerIntegrationParty);
        toast.success("Access pass resynced");
        await fetchSyncStatus(integration.parkerId, integration.integrationPartyId);
      } else {
        toast.error("Resync failed");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Resync failed");
    } finally {
      setResyncingId(null);
    }
  };

  const renderVerifyStatus = (integration: ServerRequestsParkerIntegrationPartyDto) => {
    if (!canSyncCheck(integration) || integration.integrationPartyId == null) return null;

    const state = syncStates[integration.integrationPartyId];
    if (!state) return null;

    if (state.status === "verifying") {
      return (
        <span
          className="d-inline-flex align-items-center gap-1 small text-muted"
          style={{ whiteSpace: "nowrap" }}
        >
          <Spinner animation="border" size="sm" style={{ width: 12, height: 12 }} />
          Verifying
        </span>
      );
    }

    if (state.status === "verified") {
      const verifiedIcon = (
        <span
          className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            backgroundColor: "color-mix(in srgb, #2f7d32 12%, white)",
            color: "#2f7d32",
            cursor: "default",
          }}
        >
          <CheckCircleFill size={14} />
        </span>
      );

      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`verified-${integration.integrationPartyId}`}>
              RFID confirmed active in the access system
            </Tooltip>
          }
        >
          {verifiedIcon}
        </OverlayTrigger>
      );
    }

    const problemLabel =
      state.status === "error" ? "Check failed" : "Not verified";

    const problemTooltip = state.canResync
      ? state.message ??
        (state.status === "error"
          ? "Could not verify RFID with the access system"
          : "RFID was not found or does not match in the access system")
      : [state.message, "Contact support to resolve this issue."]
          .filter(Boolean)
          .join(" ");

    const problemBadge = (
      <span
        className="badge rounded-pill fw-normal"
        style={{
          backgroundColor: "color-mix(in srgb, #f0ad4e 18%, white)",
          color: "#8a5a00",
          cursor: "default",
        }}
      >
        {problemLabel}
      </span>
    );

    return (
      <div className="d-inline-flex align-items-center gap-2 flex-wrap">
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`not-verified-${integration.integrationPartyId}`}>
              {problemTooltip}
            </Tooltip>
          }
        >
          {problemBadge}
        </OverlayTrigger>
        {supportsCredentialUpdate(integration) && state.canResync && (
          <Button
            className="btn btn-sm py-0 px-2"
            disabled={resyncingId === integration.integrationPartyId}
            onClick={() => handleResync(integration)}
          >
            Resync
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="border rounded mb-4 overflow-hidden">
      <div
        className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
        style={{ backgroundColor: "color-mix(in srgb, var(--color-light) 35%, white)" }}
      >
        <div
          className="rounded d-flex align-items-center justify-content-center flex-shrink-0 text-white"
          style={{ width: 32, height: 32, backgroundColor: "var(--color-mid)" }}
        >
          <KeyFill />
        </div>
        <strong style={{ color: "var(--color-very-dark)" }}>Access Passes</strong>
      </div>

      <div className="p-3 d-flex flex-column gap-2">
        {showManagedViaParkersMessage ? (
          <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0">
            <InfoCircleFill color="warning" className="mt-1 flex-shrink-0" />
            <div className="small">
              Access passes are managed via individual parkers. 
            </div>
          </div>
        ) : integrations.length > 0 ? (
          integrations.map((integration) => {
            const hasRfid = !!integration.metaFlashRFIDNumber?.trim();
            // CardLogger (SuppressQr): active but no RFID yet — prompt Add, hide misleading Inactive toggle
            const needsCardCredential =
              suppressesQr(integration) &&
              !!integration.isActive &&
              !hasRfid;
            const effectiveActive =
              integration.isActive &&
              !(suppressesQr(integration) && !hasRfid);
            return (
            <div
              key={`${integration.integrationPartyId}-${integration.parkerId}`}
              className="border rounded px-3 py-2 bg-white access-pass-card"
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 gap-md-3">
                <div className="d-flex flex-column gap-1">
                  <div className="d-flex align-items-center flex-wrap gap-2 gap-md-3">
                    <LightningChargeFill
                      className="flex-shrink-0"
                      style={{ color: "var(--color-mid)" }}
                    />
                    <span
                      className="fw-bold"
                      style={{ color: "var(--color-very-dark)" }}
                    >
                      {getIntegrationDisplayName(integration.integrationName)}
                    </span>
                    <span className="text-muted small">
                      {hasRfid
                        ? `RFID ${integration.metaFlashRFIDNumber}`
                        : "RFID —"}
                    </span>
                    {renderVerifyStatus(integration)}
                    {canEditRfid(integration) && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 border-0 text-decoration-none d-inline-flex align-items-center"
                        style={{ color: "var(--color-mid)" }}
                        onClick={() => setEditRfidIntegration(integration)}
                      >
                        <Pencil size={12} className="me-1 icon-shift-up" />
                        {needsCardCredential ? "Add" : "Edit"}
                      </button>
                    )}
                  </div>
                  {getIntegrationDescription(integration.integrationName) && (
                    <span className="text-muted small ps-1" style={{ fontSize: "0.75rem", opacity: 0.9 }}>
                      {getIntegrationDescription(integration.integrationName)}
                    </span>
                  )}
                </div>

                <div className="d-flex align-items-center gap-3 flex-shrink-0">
                  {!needsCardCredential && (
                  <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      role="switch"
                      id={`switch-${integration.integrationPartyId}-${integration.parkerId}`}
                      checked={effectiveActive}
                      onChange={() => {
                        const newStatus = integration.isActive
                          ? IntegrationStatus.Deactivated
                          : IntegrationStatus.Activated;

                        setPendingToggle({
                          integrationPartyId: integration.integrationPartyId!,
                          subscriptionId,
                          parkerId: integration.parkerId!,
                          facilityId,
                          newStatus,
                        });
                        setShowToggleConfirm(true);
                      }}
                    />
                    <label
                      className="form-check-label text-muted small mb-0"
                      htmlFor={`switch-${integration.integrationPartyId}-${integration.parkerId}`}
                    >
                      {effectiveActive ? "Active" : "Inactive"}
                    </label>
                  </div>
                  )}

                  {(integration.isActive || integration.parkerId === 0) &&
                    hasRfid &&
                    !suppressesQr(integration) && (
                      <Button
                        className="rounded-pill py-1 px-3 bg-white text-dark border-2"
                        style={{ borderColor: "var(--color-mid)" }}
                        onClick={() => {
                          setQrCodeData(integration.metaFlashRFIDNumber!);
                          setShowQrModal(true);
                        }}
                      >
                        <QrCode size={14} 
                          className="me-1 icon-shift-up"
                        />
                        QR code
                      </Button>
                    )}
                </div>
              </div>
            </div>
            );
          })
        ) : (
          <div className="text-muted small mb-2">No active integrations.</div>
        )}
      </div>

      {editRfidIntegration && (
        <EditRfidModal
          show={!!editRfidIntegration}
          setShow={(show) => !show && setEditRfidIntegration(null)}
          integration={editRfidIntegration}
          subscriptionId={subscriptionId}
          parkerId={editRfidIntegration.parkerId!}
          facilityId={facilityId}
          onSuccess={(updated) => {
            handleRfidUpdated(updated);
            setEditRfidIntegration(null);
          }}
        />
      )}
      <style>{`
        .access-pass-row {
          min-height: 2rem;
        }

        .access-pass-actions {
          align-self: center;
        }

        .access-pass-switch .form-check-input {
          float: none;
          margin-top: 0;
          margin-bottom: 0;
        }

        .access-pass-switch-label {
          display: flex;
          align-items: center;
          min-width: 3.25rem;
          line-height: 1;
        }
      `}</style>
    </div>
  );
}
